import { Button, Hide, VStack } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAsync } from 'react-use';
import tw from 'twin.macro';

import { createFetcher } from '../../lib/fetcher';
import {
  CreateAnnualReport,
  CreateCalculationReport,
  CreateMonthlyReport,
  CreateSingleReport
} from '../../models';
import { useReportStore } from '../../providers';
import { Card, CardFooter, CardHeader } from '../Card';
import {
  EquityCharts,
  MonthlyExpensesChart,
  OverLifeChart,
  PropertyInputs,
  ReportHeader,
  ReportTable,
  SingleVariableReport
} from '.';
import ReportSummary from './ReportSummary';

const Container = styled(Card)`
  ${tw`m-4`}
`;

const HorizontalRule = styled.hr`
  ${tw`my-2 border-neutral-700`}
`;

export function Report() {
  const { purchase, revenue, expenses, growthRates, location } = useReportStore();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [monthlyReport, setMonthlyReport] = useState<CreateMonthlyReport>();
  const [annualReport, setAnnualReport] = useState<CreateAnnualReport>();
  const [singleReport, setSingleReport] = useState<CreateSingleReport>();

  const [isDataView, setIsDataView] = useState<boolean>(false);

  const { id } = useParams();

  useAsync(async () => {
    if (purchase && revenue && expenses) {
      const { result } = await createReportFetcher({
        ...purchase,
        ...revenue,
        ...expenses,
        ...growthRates
      });

      const _report = result as CreateCalculationReport;

      if (result) {
        setMonthlyReport({
          monthlyRevenues: _report.monthlyRevenues,
          monthlyExpenses: _report.monthlyExpenses,
          monthlyCashFlow: _report.monthlyCashFlow
        });
        setAnnualReport({
          annualRevenues: _report.annualRevenues,
          annualExpenses: _report.annualExpenses,
          annualCashFlow: _report.annualCashFlow,
          homeValues: _report.homeValues,
          principalsRemaining: _report.principalsRemaining,
          equities: _report.equities,
          equityLessCashInvested: _report.equityLessCashInvested,
          cumulativeCashFlowForLoanTerm: _report.cumulativeCashFlowForLoanTerm,
          profitsAtSale: _report.profitsAtSale,
          totalReturns: _report.totalReturns,
          cashOnCashReturn: _report.cashOnCashReturn,
          simpleROI: _report.simpleROI,
          capRate: _report.capRate
        });
        setSingleReport({
          principal: _report.principal,
          monthlyPaymentWithTaxesAndInsurance: _report.monthlyPaymentWithTaxesAndInsurance,
          sumOfPayments: _report.sumOfPayments,
          interestCost: _report.interestCost,
          cashToClose: _report.cashToClose
        });
      }
    }
  }, []);

  const onSubmit = useCallback(async () => {
    if (singleReport && monthlyReport && annualReport && id) {
      setIsLoading(true);
      await createCalculationReportFetcher({
        reportInputId: id,
        ...singleReport,
        ...monthlyReport,
        ...annualReport
      }).then(() => setIsLoading(false));
    }
  }, [singleReport, monthlyReport, annualReport, id]);

  return (
    <Container>
      {!isDataView ? (
        <>
          <div>
            {location ? (
              <ReportHeader monthlyCashFlow={monthlyReport?.monthlyCashFlow} {...location} />
            ) : null}
          </div>
          <div>
            {singleReport && annualReport ? (
              <ReportSummary
                {...singleReport}
                simpleROI={annualReport.simpleROI}
                cashOnCashReturn={annualReport.cashOnCashReturn}
              />
            ) : null}
          </div>
          <HorizontalRule />
          {annualReport && expenses && singleReport && purchase && monthlyReport ? (
            <>
              <OverLifeChart {...annualReport} />
              <HorizontalRule />
              <MonthlyExpensesChart
                {...expenses}
                {...singleReport}
                {...purchase}
                {...monthlyReport}
              />
              <HorizontalRule />
              <EquityCharts {...annualReport} />
            </>
          ) : null}
        </>
      ) : (
        <>
          <CardHeader className="mx-4">Property Report</CardHeader>
          <div>
            <PropertyInputs />
          </div>
          <HorizontalRule />
          <div>
            <SingleVariableReport singleReport={singleReport} />
          </div>
          <HorizontalRule />
          <div>
            <ReportTable monthly={monthlyReport} yearly={annualReport} />
          </div>
        </>
      )}
      <VStack my="10" mx="5" align={`${!isDataView ? 'center' : 'start'}`}>
        <Button background="#eab308" color="white" onClick={() => setIsDataView(!isDataView)}>{`${
          !isDataView ? 'View all data' : 'View report'
        }`}</Button>
      </VStack>
      <CardFooter className="justify-end">
        {/* 
          Just going to hide this button for now. 
          The logic for saving the report works, but we have no way to utilize this information currently
        */}
        <Hide>
          <Button
            isLoading={isLoading}
            background="#eab308"
            textColor="white"
            size="md"
            loadingText="Saving"
            spinnerPlacement="end"
            onClick={() => onSubmit()}
          >
            Save Report
          </Button>
        </Hide>
      </CardFooter>
    </Container>
  );
}

export default Report;

const createCalculationReportFetcher = createFetcher<CreateCalculationReport>({
  baseUrl: '/api/reports/calculations',
  method: 'POST'
});

const createReportFetcher = createFetcher<unknown>({
  baseUrl: '/api/calculator/generate-calculation-results',
  method: 'POST'
});
